import { useState, useMemo } from "react";
import { Table, Text, Group, UnstyledButton } from "@mantine/core";
import { IconChevronUp, IconChevronDown, IconSelector } from "@tabler/icons-react";

/**
 * Compara dos valores. Los vacíos (null / undefined / "") siempre quedan
 * al final, sin importar la dirección del orden.
 */
export const compareValues = (a, b) => {
    const aEmpty = a === null || a === undefined || a === "";
    const bEmpty = b === null || b === undefined || b === "";
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    if (typeof a === "string" || typeof b === "string") {
        return String(a).localeCompare(String(b), "es", { sensitivity: "base" });
    }
    return a - b;
};

/**
 * Ordenamiento de tablas por columna.
 *
 * @param {Array}  data       filas a ordenar (no se muta)
 * @param {Object} accessors  mapa { campo: (fila) => valor comparable }
 * @param {string} initial    columna activa al montar (opcional)
 *
 * @returns { sorted, thProps, sortBy, direction }
 */
export function useTableSort(data, accessors, initial = null) {
    const [sortBy, setSortBy] = useState(initial);
    const [direction, setDirection] = useState("desc");

    const handleSort = (field) => {
        if (sortBy === field) {
            // Misma columna: alterna desc -> asc -> desc...
            setDirection((d) => (d === "desc" ? "asc" : "desc"));
        } else {
            // Columna nueva: arranca en descendente
            setSortBy(field);
            setDirection("desc");
        }
    };

    const sorted = useMemo(() => {
        const list = data || [];
        const accessor = sortBy ? accessors[sortBy] : null;
        if (!accessor) return list;

        // Copia: nunca mutar la prop
        return [...list].sort((a, b) => {
            const result = compareValues(accessor(a), accessor(b));
            return direction === "asc" ? result : -result;
        });
    }, [data, accessors, sortBy, direction]);

    return {
        sorted,
        sortBy,
        direction,
        thProps: { sortBy, direction, onSort: handleSort },
    };
}

/**
 * Encabezado clickeable con indicador de dirección.
 * Se usa igual que un Table.Th: <SortableTh field="lost" {...thProps}>Perdidos</SortableTh>
 */
export function SortableTh({ field, sortBy, direction, onSort, align = "left", children, style }) {
    const active = sortBy === field;
    const Icon = active ? (direction === "asc" ? IconChevronUp : IconChevronDown) : IconSelector;

    return (
        <Table.Th style={style}>
            <UnstyledButton
                onClick={() => onSort(field)}
                aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"}
                style={{ width: "100%" }}
            >
                <Group
                    gap={4}
                    wrap="nowrap"
                    justify={align === "center" ? "center" : "flex-start"}
                >
                    <Text size="sm" fw={700}>
                        {children}
                    </Text>
                    <Icon size={14} stroke={1.8} style={{ opacity: active ? 1 : 0.35, flexShrink: 0 }} />
                </Group>
            </UnstyledButton>
        </Table.Th>
    );
}